import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { Grid, Stack } from '@mui/material'
import { calendarFiltersAtom } from '../../atoms/calendarAtom'
import UserEventApi from '../../api/UserEventApi'
import DropdownSelect from '../components/DropdownSelect'
import { Marketplace } from '../../types/Marketplace'
import DropdownTreeView, { DropdownTreeViewItems } from '../issues/filters/DropdownTreeView'
import { IssueEntity } from '../../types/IssueEntity'
import { WarrantEntity } from '../../types/WarrantEntity'
import MarketplacesApi from '../../api/MarketplacesApi'
import { getAdvisors } from './utils'
import ToggleWatchLists from '../components/ToggleWatchLists'
import ToggleIssueTypes from '../components/ToggleIssueTypes'

type CalendarFilter = typeof calendarFiltersAtom

type CalendarFilterComponent = React.FC<{ entities: Array<IssueEntity | WarrantEntity> }>

const listedAlternatives = [
    { id: 'all', label: 'Visa alla optioner' },
    { id: 'only-listed', label: 'Visa enbart noterade optioner' },
    { id: 'only-unlisted', label: 'Visa enbart onoterade optioner' },
]

const completedAlternatives = [
    { id: 'all', label: 'Visa samtliga' },
    { id: 'only-completed', label: 'Visa enbart genomförda emissioner' },
    { id: 'not-completed', label: 'Dölj genomförda emissioner' },
]
const api = new MarketplacesApi()

export const CalendarFilter: CalendarFilterComponent = ({ entities }) => {
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithEntities, setMarketplacesWithEntities] = useState<string[]>([])
    const { financialAdvisors } = useMemo(() => getAdvisors(entities, 1), [entities])
    const [filters, setFilters] = useAtom(calendarFiltersAtom)

    useEffect(() => {
        const fetchMarkets = async () => {
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!entities.length) return

        const marketplaces = new Set<string>()

        entities.forEach((entity) => {
            marketplaces.add(entity.marketplaceId)
        })

        setMarketplacesWithEntities([...marketplaces])
    }, [entities])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithEntities.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {} as DropdownTreeViewItems)

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithEntities])

    const handleFilterChange = useCallback(
        async (filterType: string, selectedItems: boolean | (string | Date)[]) => {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [filterType]: { value: selectedItems },
            }))

            const userEventApi = new UserEventApi()
            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.FILTER_CALENDAR, {
                filterType,
                selectedItems,
            })
        },
        [setFilters],
    )

    const handleIssueTypeFilterChange = useCallback((v) => handleFilterChange('issueTypes', v), [handleFilterChange])
    return (
        <Stack direction='row'>
            <Stack height={116} width={230} pr={3}>
                <ToggleIssueTypes
                    onChange={handleIssueTypeFilterChange}
                    value={filters.issueTypes.value}
                    excludeTypes={['Directed issue', 'Non-cash issue', 'Convertible']}
                />
            </Stack>
            <Grid container spacing={2} sx={{ width: '100%' }} alignItems='start'>
                <Grid size={{ xs: 4, lg: 3 }} alignContent='center'>
                    <DropdownSelect
                        label='Genomförda emissioner och optioner'
                        items={completedAlternatives}
                        selectedItems={filters.completed.value}
                        onChange={(selectedItems) => {
                            void handleFilterChange('completed', selectedItems)
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 4, lg: 3 }} alignContent='center'>
                    <DropdownSelect
                        label='Onoterade optioner'
                        items={listedAlternatives}
                        selectedItems={filters.listed.value}
                        onChange={(selectedItems) => {
                            void handleFilterChange('listed', selectedItems)
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 4, lg: 3 }} alignContent='center'>
                    <DropdownTreeView
                        label='Listor'
                        pluralLabel='Listor'
                        items={filteredMarketplaces}
                        selectedItems={filters.marketplaces.value}
                        onChange={(selectedItems) => void handleFilterChange('marketplaces', selectedItems)}
                    />
                </Grid>
                <Grid size={{ xs: 4, lg: 3 }} alignContent='center'>
                    <DropdownSelect
                        label='Finansiell rådgivare'
                        items={financialAdvisors}
                        multiple
                        selectedItems={filters.financialAdvisors.value}
                        onChange={(selectedItems) => void handleFilterChange('financialAdvisors', selectedItems)}
                    />
                </Grid>
                <Grid size={{ xs: 4, lg: 3 }} alignContent='center'>
                    <ToggleWatchLists
                        onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                        value={filters.onlyInWatchLists.value}
                    />
                </Grid>
            </Grid>
        </Stack>
    )
}
